(() => {
  if (customElements.get('hc-order-builder')) return;

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);

  const unique = (values) => [...new Set(values)];

  class HCOrderBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mounted === 'true') return;
      this.dataset.mounted = 'true';

      try {
        this.config = JSON.parse(this.querySelector('[data-hc-config]').textContent);
        this.state = {
          step: 0,
          layout: '',
          delivery: '',
          fields: {},
          addons: {},
          accepted: false,
          busy: false,
          error: ''
        };
        this.config.blocks.forEach((block) => {
          if (block.type === 'text_field' || block.type === 'choice_field') {
            this.state.fields[block.key] = block.defaultValue || '';
          }
        });
        this.render();
      } catch (error) {
        this.showFatalError();
      }
    }

    get steps() {
      return this.config.steps;
    }

    get productVariants() {
      return this.config.product.variants || [];
    }

    get layouts() {
      return unique(this.productVariants.map((variant) => variant.options[0]).filter(Boolean));
    }

    get deliveries() {
      return unique(this.productVariants.map((variant) => variant.options[1]).filter(Boolean));
    }

    get selectedVariant() {
      return this.productVariants.find((variant) =>
        variant.options[0] === this.state.layout &&
        variant.options[1] === this.state.delivery
      );
    }

    get isDigitalOnly() {
      const value = String(this.state.delivery || '').toLowerCase();
      return value.includes('digital') &&
        (value.includes('only') || value.includes('no product'));
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale, {
        style: 'currency',
        currency: this.config.currency
      }).format((Number(cents) || 0) / 100);
    }

    deliveryCopy(value) {
      const lower = String(value).toLowerCase();
      if (
        lower.includes('digital') &&
        (lower.includes('only') || lower.includes('no product'))
      ) {
        return {
          label: this.config.labels.deliveryDigital,
          help: this.config.labels.deliveryDigitalHelp
        };
      }
      if (lower.includes('and') || lower.includes('digital copy and')) {
        return {
          label: this.config.labels.deliveryBoth,
          help: this.config.labels.deliveryBothHelp
        };
      }
      if (lower.includes('printed') || lower.includes('mailed')) {
        return {
          label: this.config.labels.deliveryPrint,
          help: this.config.labels.deliveryPrintHelp
        };
      }
      return {
        label: this.config.labels.deliveryDigital,
        help: this.config.labels.deliveryDigitalHelp
      };
    }

    variantForDelivery(delivery) {
      return this.productVariants.find((variant) =>
        variant.options[0] === (this.state.layout || this.layouts[0]) &&
        variant.options[1] === delivery
      );
    }

    parseChoices(value) {
      return String(value || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [optionValue, label, help] = line.split('|').map((part) => part.trim());
          return {
            value: optionValue,
            label: label || optionValue,
            help: help || ''
          };
        });
    }

    visibleBlock(block) {
      if (block.showFor === 'mailed' && this.isDigitalOnly) return false;
      if (block.showFor === 'digital' && !this.isDigitalOnly) return false;
      if (!block.conditionKey) return true;

      const fieldValue = this.state.fields[block.conditionKey];
      const addonValue = this.state.addons[block.conditionKey];
      const actual = fieldValue !== undefined ? fieldValue : addonValue;

      if (block.conditionValue === 'selected') return Boolean(actual);
      if (block.conditionValue === 'not_selected') return !actual;
      return String(actual ?? '') === String(block.conditionValue);
    }

    blocksForStep(stepId) {
      return this.config.blocks.filter((block) =>
        block.step === stepId && this.visibleBlock(block)
      );
    }

    selectedAddonVariants() {
      const selected = [];
      const blocksByGroup = this.addonGroups();

      Object.entries(this.state.addons).forEach(([group, selection]) => {
        if (!selection) return;
        const blocks = blocksByGroup[group] || [];

        blocks.forEach((block) => {
          const variant = (block.product?.variants || []).find((item) =>
            String(item.id) === String(selection)
          );
          if (variant) selected.push({ block, variant });
        });
      });

      return selected;
    }

    addonGroups() {
      return this.config.blocks
        .filter((block) => block.type === 'addon' && this.visibleBlock(block))
        .reduce((groups, block) => {
          groups[block.group] ||= [];
          groups[block.group].push(block);
          return groups;
        }, {});
    }

    totalCents() {
      const base = this.selectedVariant?.price || this.productVariants[0]?.price || 0;
      return this.selectedAddonVariants().reduce(
        (sum, item) => sum + Number(item.variant.price || 0),
        Number(base)
      );
    }

    layoutImage(index) {
      return this.config.product.images[index] || this.config.product.images[0] || null;
    }

    choiceButton({ name, value, label, help, price, selected, image }) {
      return `
        <button
          type="button"
          class="hcob-choice ${selected ? 'is-selected' : ''}"
          data-choice="${escapeHtml(name)}"
          data-value="${escapeHtml(value)}"
          aria-pressed="${selected ? 'true' : 'false'}">
          ${image ? `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" loading="lazy" width="360" height="280">` : ''}
          <span class="hcob-choice__copy">
            <strong>${escapeHtml(label)}</strong>
            ${help ? `<small>${escapeHtml(help)}</small>` : ''}
          </span>
          ${price !== undefined ? `<span class="hcob-choice__price">${this.money(price)}</span>` : ''}
          <span class="hcob-choice__check" aria-hidden="true">✓</span>
        </button>
      `;
    }

    renderLayout() {
      const selectedIndex = Math.max(0, this.layouts.indexOf(this.state.layout));
      const selectedImage = this.layoutImage(selectedIndex);
      const selectedLabel = this.state.layout || this.layouts[0] || '';

      return `
        ${this.config.showDesignPreview && selectedImage ? `
          <figure class="hcob-design-preview" aria-live="polite">
            <img
              src="${escapeHtml(selectedImage.src)}"
              alt="${escapeHtml(selectedImage.alt)}"
              width="900"
              height="690">
            <figcaption>${escapeHtml(selectedLabel)}</figcaption>
          </figure>
        ` : ''}
        <div class="hcob-layout-grid" role="group" aria-label="${escapeHtml(this.config.labels.gallery)}">
          ${this.layouts.map((layout, index) => {
            const image = this.layoutImage(index);
            const selected = this.state.layout === layout;
            return `
              <button
                type="button"
                class="hcob-layout-thumb ${selected ? 'is-selected' : ''}"
                data-choice="layout"
                data-value="${escapeHtml(layout)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                aria-label="${escapeHtml(this.config.labels.viewDesign)}: ${escapeHtml(layout)}">
                <img
                  src="${escapeHtml(image?.src || '')}"
                  alt=""
                  loading="${index === 0 ? 'eager' : 'lazy'}"
                  width="180"
                  height="138">
                <span>${escapeHtml(layout)}</span>
              </button>
            `;
          }).join('')}
        </div>
      `;
    }

    renderDelivery() {
      return `
        <div class="hcob-choice-stack">
          ${this.deliveries.map((delivery) => {
            const copy = this.deliveryCopy(delivery);
            const variant = this.variantForDelivery(delivery);
            return this.choiceButton({
              name: 'delivery',
              value: delivery,
              label: copy.label,
              help: copy.help,
              price: variant?.price,
              selected: this.state.delivery === delivery
            });
          }).join('')}
        </div>
        <p class="hcob-note">${escapeHtml(this.config.labels.shippingNote)}</p>
      `;
    }

    renderTextField(block) {
      const value = this.state.fields[block.key] || '';
      const required = block.required ? '<b aria-hidden="true"> *</b>' : '';
      const optional = block.required ? '' : ` <em>${escapeHtml(this.config.labels.optional)}</em>`;

      if (block.inputType === 'textarea') {
        return `
          <label class="hcob-field" ${block.shopifyAttributes || ''}>
            <span>${escapeHtml(block.label)}${required}${optional}</span>
            ${block.help ? `<small>${escapeHtml(block.help)}</small>` : ''}
            <textarea data-field="${escapeHtml(block.key)}" rows="3" placeholder="${escapeHtml(block.placeholder)}">${escapeHtml(value)}</textarea>
          </label>
        `;
      }

      return `
        <label class="hcob-field" ${block.shopifyAttributes || ''}>
          <span>${escapeHtml(block.label)}${required}${optional}</span>
          ${block.help ? `<small>${escapeHtml(block.help)}</small>` : ''}
          <input
            type="${escapeHtml(block.inputType || 'text')}"
            data-field="${escapeHtml(block.key)}"
            value="${escapeHtml(value)}"
            placeholder="${escapeHtml(block.placeholder)}">
        </label>
      `;
    }

    renderChoiceField(block) {
      const choices = this.parseChoices(block.options);
      if (block.control === 'select') {
        const selectedValue = this.state.fields[block.key] || '';
        return `
          <label class="hcob-field hcob-field--select" ${block.shopifyAttributes || ''}>
            <span>${escapeHtml(block.label)}${block.required ? '<b aria-hidden="true"> *</b>' : ''}</span>
            ${block.help ? `<small>${escapeHtml(block.help)}</small>` : ''}
            <select data-field="${escapeHtml(block.key)}">
              <option value="">${escapeHtml(this.config.labels.selectOne)}</option>
              ${choices.map((choice) => `
                <option value="${escapeHtml(choice.value)}" ${selectedValue === choice.value ? 'selected' : ''}>
                  ${escapeHtml(choice.label)}
                </option>
              `).join('')}
            </select>
          </label>
        `;
      }
      return `
        <div class="hcob-question" ${block.shopifyAttributes || ''}>
          <span>${escapeHtml(block.label)}${block.required ? '<b aria-hidden="true"> *</b>' : ''}</span>
          ${block.help ? `<small>${escapeHtml(block.help)}</small>` : ''}
          <div class="hcob-choice-stack">
            ${choices.map((choice) => this.choiceButton({
              name: `field:${block.key}`,
              value: choice.value,
              label: choice.label,
              help: choice.help,
              selected: this.state.fields[block.key] === choice.value
            })).join('')}
          </div>
        </div>
      `;
    }

    renderNotice(block) {
      return `
        <div class="hcob-notice" ${block.shopifyAttributes || ''}>
          <span>${escapeHtml(block.label)}</span>
          ${block.help ? `<small>${escapeHtml(block.help)}</small>` : ''}
        </div>
      `;
    }

    renderAddonGroup(group, blocks) {
      const selection = this.state.addons[group] || '';
      const first = blocks[0];
      const variants = blocks.flatMap((block) =>
        (block.product?.variants || []).map((variant) => ({ block, variant }))
      );
      const isToggle = first.control === 'toggle' && variants.length === 1;

      if (isToggle) {
        const item = variants[0];
        return `
          <div class="hcob-addon" ${first.shopifyAttributes || ''}>
            <div>
              <span>${escapeHtml(first.label)}</span>
              ${first.help ? `<small>${escapeHtml(first.help)}</small>` : ''}
            </div>
            <label class="hcob-switch">
              <input
                type="checkbox"
                data-addon-group="${escapeHtml(group)}"
                data-addon-value="${escapeHtml(item.variant.id)}"
                ${selection ? 'checked' : ''}>
              <span aria-hidden="true"></span>
              <b>+${this.money(item.variant.price)}</b>
            </label>
          </div>
        `;
      }

      return `
        <div class="hcob-addon-group" ${first.shopifyAttributes || ''}>
          <div class="hcob-addon-heading">
            <span>${escapeHtml(first.groupLabel || first.label)}</span>
            ${first.groupHelp || first.help ? `<small>${escapeHtml(first.groupHelp || first.help)}</small>` : ''}
          </div>
          <div class="hcob-choice-stack">
            ${this.choiceButton({
              name: `addon:${group}`,
              value: '',
              label: this.config.labels.none,
              selected: !selection
            })}
            ${variants.map(({ block, variant }) => this.choiceButton({
              name: `addon:${group}`,
              value: variant.id,
              label: variants.length === 1 || blocks.length > 1 ? block.label : variant.title,
              help: blocks.length > 1 ? block.help : '',
              price: variant.price,
              selected: String(selection) === String(variant.id)
            })).join('')}
          </div>
        </div>
      `;
    }

    renderConfiguredStep(stepId) {
      const blocks = this.blocksForStep(stepId);
      const groupsRendered = new Set();

      return `
        <div class="hcob-fields">
          ${blocks.map((block) => {
            if (block.type === 'text_field') return this.renderTextField(block);
            if (block.type === 'choice_field') return this.renderChoiceField(block);
            if (block.type === 'notice') return this.renderNotice(block);
            if (block.type === 'addon') {
              if (groupsRendered.has(block.group)) return '';
              groupsRendered.add(block.group);
              return this.renderAddonGroup(
                block.group,
                blocks.filter((item) => item.type === 'addon' && item.group === block.group)
              );
            }
            return '';
          }).join('')}
        </div>
      `;
    }

    reviewRows() {
      const rows = [
        [this.config.labels.reviewDesign, this.state.layout],
        [this.config.labels.reviewDelivery, this.deliveryCopy(this.state.delivery).label]
      ];

      this.config.blocks.forEach((block) => {
        if (
          (block.type === 'text_field' || block.type === 'choice_field') &&
          this.visibleBlock(block) &&
          this.state.fields[block.key]
        ) {
          const choice = block.type === 'choice_field'
            ? this.parseChoices(block.options).find((item) => item.value === this.state.fields[block.key])
            : null;
          rows.push([block.propertyName || block.label, choice?.label || this.state.fields[block.key]]);
        }
      });

      const addons = this.selectedAddonVariants();
      rows.push([
        this.config.labels.reviewExtras,
        addons.length
          ? addons.map(({ block, variant }) =>
            `${block.label}${block.product.variants.length > 1 ? ` — ${variant.title}` : ''} (+${this.money(variant.price)})`
          ).join(' · ')
          : this.config.labels.none
      ]);

      return rows;
    }

    renderReview() {
      const imageIndex = Math.max(0, this.layouts.indexOf(this.state.layout));
      const image = this.layoutImage(imageIndex);
      return `
        <div class="hcob-review">
          <div class="hcob-review__visual">
            ${image ? `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" width="220" height="170">` : ''}
            <div>
              <span>${escapeHtml(this.config.product.title)}</span>
              <strong>${escapeHtml(this.state.layout)}</strong>
            </div>
          </div>
          <div class="hcob-review__rows">
            ${this.reviewRows().map(([label, value]) => `
              <div><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>
            `).join('')}
          </div>
        </div>
        <label class="hcob-terms">
          <input type="checkbox" data-accepted ${this.state.accepted ? 'checked' : ''}>
          <span>${escapeHtml(this.config.labels.terms)}</span>
        </label>
      `;
    }

    currentBody() {
      const step = this.steps[this.state.step];
      if (step.id === 'design') return this.renderLayout();
      if (step.id === 'delivery') return this.renderDelivery();
      if (step.id === 'review') return this.renderReview();
      return this.renderConfiguredStep(step.id);
    }

    validateStep() {
      this.state.error = '';
      const step = this.steps[this.state.step];

      if (step.id === 'design' && !this.state.layout) {
        this.state.error = this.config.labels.chooseDesignError;
      } else if (step.id === 'delivery' && !this.state.delivery) {
        this.state.error = this.config.labels.chooseDeliveryError;
      } else {
        const missing = this.blocksForStep(step.id).find((block) =>
          (block.type === 'text_field' || block.type === 'choice_field') &&
          block.required &&
          !String(this.state.fields[block.key] || '').trim()
        );
        if (missing) {
          this.state.error = this.config.labels.requiredError.replace('[field]', missing.label);
        }
      }

      return !this.state.error;
    }

    mainProperties(group) {
      const properties = {
        '_assistant_order_group': group,
        '_assistant_version': this.config.version
      };

      this.config.blocks.forEach((block) => {
        if (
          (block.type === 'text_field' || block.type === 'choice_field') &&
          this.visibleBlock(block)
        ) {
          properties[block.propertyName || block.label] = this.state.fields[block.key] || '';
        }
      });

      this.selectedAddonVariants().forEach(({ block, variant }) => {
        properties[block.propertyName || block.label] =
          block.product.variants.length > 1 ? variant.title : 'Yes';
      });

      properties[this.config.labels.termsProperty] = this.config.labels.termsAccepted;
      return properties;
    }

    cartPayload() {
      const group = `HC-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const items = [{
        id: this.selectedVariant.id,
        quantity: 1,
        properties: this.mainProperties(group)
      }];

      this.selectedAddonVariants().forEach(({ block, variant }) => {
        items.push({
          id: variant.id,
          quantity: 1,
          properties: {
            '_assistant_parent_order_group': group,
            '_assistant_field_name': block.propertyName || block.label
          }
        });
      });

      return { group, items, expectedTotal: this.totalCents() };
    }

    async removeGroup(root, group) {
      try {
        const cart = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        }).then((response) => response.json());
        const updates = {};
        cart.items.forEach((item) => {
          const properties = item.properties || {};
          if (
            properties._assistant_order_group === group ||
            properties._assistant_parent_order_group === group
          ) {
            updates[item.key] = 0;
          }
        });
        if (Object.keys(updates).length) {
          await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          });
        }
      } catch (_) {
        // The shopper still sees a clear error and can retry or review the cart.
      }
    }

    async addToCart() {
      if (!this.state.accepted) {
        this.state.error = this.config.labels.termsError;
        this.render();
        return;
      }
      if (!this.selectedVariant) {
        this.state.error = this.config.labels.variantError;
        this.render();
        return;
      }

      this.state.busy = true;
      this.state.error = '';
      this.render();

      const payload = this.cartPayload();
      const root = window.Shopify?.routes?.root || '/';

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items })
        });
        if (!addResponse.ok) {
          const problem = await addResponse.json().catch(() => ({}));
          throw new Error(problem.description || this.config.labels.cartError);
        }

        const cartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error(this.config.labels.verifyError);

        const cart = await cartResponse.json();
        const lines = cart.items.filter((item) => {
          const properties = item.properties || {};
          return properties._assistant_order_group === payload.group ||
            properties._assistant_parent_order_group === payload.group;
        });
        const actualTotal = lines.reduce((sum, item) => sum + item.final_line_price, 0);

        if (lines.length !== payload.items.length || actualTotal !== payload.expectedTotal) {
          await this.removeGroup(root, payload.group);
          throw new Error(this.config.labels.verifyError);
        }

        window.location.assign(`${root}cart`);
      } catch (error) {
        this.state.busy = false;
        this.state.error = error.message || this.config.labels.cartError;
        this.render();
      }
    }

    bind() {
      this.querySelectorAll('[data-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          const name = button.dataset.choice;
          const value = button.dataset.value;

          if (name === 'layout') {
            this.state.layout = value;
            this.updateGallery(value);
          } else if (name === 'delivery') {
            this.state.delivery = value;
            if (this.isDigitalOnly) {
              Object.keys(this.state.addons).forEach((group) => {
                const mailedOnly = this.addonGroups()[group]?.some((block) => block.showFor === 'mailed');
                if (mailedOnly) this.state.addons[group] = '';
              });
            }
          } else if (name.startsWith('field:')) {
            this.state.fields[name.slice(6)] = value;
          } else if (name.startsWith('addon:')) {
            this.state.addons[name.slice(6)] = value;
          }

          this.state.error = '';
          this.render();
        });
      });

      this.querySelectorAll('[data-field]').forEach((input) => {
        input.addEventListener(input.tagName === 'SELECT' ? 'change' : 'input', () => {
          this.state.fields[input.dataset.field] = input.value;
        });
      });

      this.querySelectorAll('[data-addon-group]').forEach((input) => {
        input.addEventListener('change', () => {
          this.state.addons[input.dataset.addonGroup] =
            input.checked ? input.dataset.addonValue : '';
          this.render();
        });
      });


      this.querySelector('[data-hc-back]')?.addEventListener('click', () => {
        this.state.step = Math.max(0, this.state.step - 1);
        this.state.error = '';
        this.render();
      });

      this.querySelector('[data-hc-next]')?.addEventListener('click', () => {
        if (!this.validateStep()) {
          this.render();
          return;
        }
        this.state.step = Math.min(this.steps.length - 1, this.state.step + 1);
        this.render();
      });

      this.querySelector('[data-hc-add]')?.addEventListener('click', () => this.addToCart());
      this.querySelector('[data-accepted]')?.addEventListener('change', (event) => {
        this.state.accepted = event.currentTarget.checked;
      });
    }

    updateGallery(layout) {
      const index = this.layouts.indexOf(layout);
      const image = this.layoutImage(index);
      const section = this.closest('[data-hc-product-section]');
      const mainImage = section?.querySelector('[data-hc-main-image]');
      if (!image || !mainImage) return;
      mainImage.src = image.src;
      mainImage.alt = image.alt;
      section.querySelectorAll('[data-hc-thumbnail]').forEach((button, buttonIndex) => {
        button.classList.toggle('is-selected', buttonIndex === index);
      });
    }

    render() {
      const current = this.steps[this.state.step];
      const percent = Math.round(((this.state.step + 1) / this.steps.length) * 100);
      const buttonTotal = this.money(this.totalCents());

      this.querySelector('[data-hc-app]').innerHTML = `
        <section class="hcob-card" aria-labelledby="hcob-title-${escapeHtml(this.config.sectionId)}">
          <header class="hcob-header">
            <div class="hcob-guide">
              <span class="hcob-mark" aria-hidden="true">HC</span>
              <div>
                <strong>${escapeHtml(this.config.labels.assistantTitle)}</strong>
                <small>${escapeHtml(this.config.labels.stepLabel)} ${this.state.step + 1} ${escapeHtml(this.config.labels.ofLabel)} ${this.steps.length}</small>
              </div>
            </div>
            <div class="hcob-total">
              <span>${escapeHtml(this.config.labels.orderTotal)}</span>
              <strong>${buttonTotal}</strong>
            </div>
          </header>
          <div
            class="hcob-progress"
            role="progressbar"
            aria-valuemin="1"
            aria-valuemax="${this.steps.length}"
            aria-valuenow="${this.state.step + 1}">
            <span style="width:${percent}%"></span>
          </div>
          <div class="hcob-content">
            <div class="hcob-chat">
              <span class="hcob-avatar" aria-hidden="true">✦</span>
              <div>
                <h2 id="hcob-title-${escapeHtml(this.config.sectionId)}">${escapeHtml(current.title)}</h2>
                <p>${escapeHtml(current.subtitle)}</p>
              </div>
            </div>
            <div class="hcob-body">${this.currentBody()}</div>
            ${this.state.error ? `<p class="hcob-error" role="alert">${escapeHtml(this.state.error)}</p>` : ''}
          </div>
          <footer class="hcob-actions">
            <button type="button" class="hcob-back" data-hc-back ${this.state.step === 0 || this.state.busy ? 'disabled' : ''}>
              ${escapeHtml(this.config.labels.back)}
            </button>
            ${this.state.step < this.steps.length - 1
              ? `<button type="button" class="hcob-next" data-hc-next>${escapeHtml(this.config.labels.continue)} <span aria-hidden="true">→</span></button>`
              : `<button type="button" class="hcob-next" data-hc-add ${this.state.busy ? 'disabled' : ''}>${this.state.busy ? escapeHtml(this.config.labels.adding) : `${escapeHtml(this.config.labels.addToCart)} · ${buttonTotal}`}</button>`
            }
          </footer>
        </section>
      `;

      this.bind();
      this.querySelector('.hcob-card')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    showFatalError() {
      const fallback = this.querySelector('[data-hc-fallback]');
      if (fallback) fallback.hidden = false;
      const app = this.querySelector('[data-hc-app]');
      if (app) app.hidden = true;
    }
  }

  customElements.define('hc-order-builder', HCOrderBuilder);

  document.addEventListener('click', (event) => {
    const thumbnail = event.target.closest('[data-hc-thumbnail]');
    if (!thumbnail) return;
    const section = thumbnail.closest('[data-hc-product-section]');
    const image = section?.querySelector('[data-hc-main-image]');
    if (!image) return;
    image.src = thumbnail.dataset.src;
    image.alt = thumbnail.dataset.alt || '';
    section.querySelectorAll('[data-hc-thumbnail]').forEach((button) => {
      button.classList.toggle('is-selected', button === thumbnail);
    });
  });
})();
